import { z } from "zod";

/**
 * Practice Interview feedback, version 2: the typed rubric shared by the server (scoring,
 * storage) and the client (coach card, results page).
 *
 * Each answer is rated on six dimensions against fixed level descriptions. Levels come from
 * Jev (TypeSafe) when TYPESAFE_API_KEY is set, with a calibrated confidence; otherwise from an
 * OpenAI structured call, which has no confidence. Counts (words, pace, fillers, "I" vs "we")
 * are computed in code, never by a model.
 */

export const PRACTICE_RUBRIC_DIMENSIONS = [
  "relevance",
  "specificity",
  "ownership",
  "result",
  "structure",
  "concision",
] as const;
export type PracticeRubricDimension = (typeof PRACTICE_RUBRIC_DIMENSIONS)[number];

export const PRACTICE_RUBRIC_LEVELS = ["Missing", "Thin", "Solid", "Strong"] as const;

type Levels = [string, string, string, string];

/** What each dimension asks and what each level means. The model sees exactly these words. */
export const PRACTICE_RUBRIC: Record<
  PracticeRubricDimension,
  { label: string; question: string; levels: Levels }
> = {
  relevance: {
    label: "Relevance",
    question: "How directly does the answer respond to the question that was asked?",
    levels: [
      "Does not address the question",
      "Touches on the question but drifts away from it",
      "Answers the question, with some drift",
      "Answers exactly what was asked",
    ],
  },
  specificity: {
    label: "Specific",
    question: "How specific and concrete is the answer?",
    levels: [
      "Only general statements, no example",
      "An example, but mostly general",
      "A concrete example with some detail",
      "A concrete example with names, numbers or other specifics",
    ],
  },
  ownership: {
    label: "Ownership",
    question:
      "How clearly does the candidate say what they personally did or decided in a specific situation, rather than what the team did or what they usually do?",
    levels: [
      "Nothing about their own actions",
      "Mostly what the team did, a little about themselves",
      "Their own part is clear in places",
      "Clearly says what they personally did and decided",
    ],
  },
  result: {
    label: "Result",
    question: "How clearly does the answer say how it ended or what changed?",
    levels: [
      "No outcome at all",
      "A vague outcome",
      "A clear outcome without evidence",
      "A clear outcome with a number or other concrete evidence",
    ],
  },
  structure: {
    label: "Structure",
    question:
      "How easy is the answer to follow in the order it was actually told? Judge the telling, not the events: restarts, jumping back and asides make it harder to follow.",
    levels: [
      "Hard to follow",
      "Some order, but it jumps around",
      "Mostly in a clear order",
      "A clear beginning, middle and end",
    ],
  },
  concision: {
    label: "Concise",
    question: "How concise is the answer for what it says?",
    levels: [
      "Rambling, with a lot of repetition",
      "Longer than it needs to be",
      "Mostly to the point",
      "Tight, no wasted words",
    ],
  },
};

/** Weights for the 0-100 answer score; the parts interviewers weigh most count more. */
export const PRACTICE_RUBRIC_WEIGHTS: Record<PracticeRubricDimension, number> = {
  relevance: 1,
  specificity: 1.2,
  ownership: 1.2,
  result: 1.3,
  structure: 0.8,
  concision: 0.5,
};

/** Below this Jev confidence a level is shown as a "close call". */
export const PRACTICE_CLOSE_CALL_CONFIDENCE = 0.6;

/** Below this answer score the interviewer offers another go after coaching; above it, moves on. */
export const PRACTICE_RETRY_BELOW_SCORE = 75;

export const practiceRubricScoreSchema = z.object({
  dimension: z.enum(PRACTICE_RUBRIC_DIMENSIONS),
  level: z.number().int().min(0).max(3),
  /** Jev confidence 0-1; null when the level came from a model without calibrated confidence. */
  confidence: z.number().min(0).max(1).nullable(),
  closeCall: z.boolean(),
});
export type PracticeRubricScore = z.infer<typeof practiceRubricScoreSchema>;

export const practiceAnswerNoteSchema = z.object({
  kind: z.enum(["keep", "change"]),
  text: z.string().max(300),
  /** Words the candidate actually said; null when the model's quote could not be found. */
  quote: z.string().max(400).nullable(),
});
export type PracticeAnswerNote = z.infer<typeof practiceAnswerNoteSchema>;

export const practiceAnswerMetricsSchema = z.object({
  words: z.number().int().min(0),
  durationSeconds: z.number().min(0).nullable(),
  wordsPerMinute: z.number().int().min(0).nullable(),
  fillers: z.object({
    total: z.number().int().min(0),
    top: z.array(z.object({ word: z.string(), count: z.number().int() })).max(3),
  }),
  hedges: z.number().int().min(0),
  iCount: z.number().int().min(0),
  weCount: z.number().int().min(0),
  hasNumber: z.boolean(),
});
export type PracticeAnswerMetrics = z.infer<typeof practiceAnswerMetricsSchema>;

/**
 * What kind of question was asked, which decides what a complete answer covers. Jev picks the
 * kind and checks every item of every checklist in the same call (they run in parallel); code
 * keeps the checklist for the kind it picked.
 */
export const PRACTICE_QUESTION_KINDS = [
  "story",
  "hypothetical",
  "technical",
  "motivation",
] as const;
export type PracticeQuestionKind = (typeof PRACTICE_QUESTION_KINDS)[number];

/** The description Jev reads for each kind. */
export const PRACTICE_QUESTION_KIND_CRITERIA: Record<PracticeQuestionKind, string> = {
  story: "Asks about something the candidate did in the past, such as 'tell me about a time...'",
  hypothetical:
    "Asks what the candidate would do in a situation, or how they would approach or decide something",
  technical:
    "Tests knowledge or skill: how something works, or how the candidate would build, design or solve something technical",
  motivation:
    "Asks about the candidate themselves: why this role or company, tell me about yourself, strengths, weaknesses or goals",
};

type ChecklistItem = { id: string; label: string; check: string };

/** One yes/no check per item; the label is what the candidate sees. */
export const PRACTICE_CHECKLISTS: Record<
  PracticeQuestionKind,
  { title: string; items: ChecklistItem[] }
> = {
  story: {
    title: "STAR",
    items: [
      {
        id: "situation",
        label: "Situation",
        check:
          "Does the answer set up one specific situation, such as when it was, where, or who was involved?",
      },
      {
        id: "task",
        label: "Task",
        check:
          "Does the answer say what the candidate was trying to achieve, or what the challenge was?",
      },
      {
        id: "action",
        label: "Action",
        check: "Does the answer say what the candidate personally did, not only what the team did?",
      },
      {
        id: "result",
        label: "Result",
        check:
          "Does the answer say concretely how it ended or what changed as a result, not only that it 'worked out'?",
      },
      {
        id: "learning",
        label: "Learning",
        check: "Does the answer say what the candidate learned, or what they would do differently?",
      },
    ],
  },
  hypothetical: {
    title: "A strong approach",
    items: [
      {
        id: "approach",
        label: "Approach first",
        check: "Does the answer start with a clear overall approach before the details?",
      },
      {
        id: "steps",
        label: "Concrete steps",
        check: "Does the answer give concrete steps in a sensible order?",
      },
      {
        id: "measure",
        label: "How you'd know it worked",
        check: "Does the answer say how the candidate would measure whether it worked?",
      },
      {
        id: "tradeoff",
        label: "A trade-off",
        check: "Does the answer weigh at least one trade-off, risk or alternative?",
      },
      {
        id: "experience",
        label: "Your experience",
        check: "Does the answer draw on something the candidate has actually done before?",
      },
    ],
  },
  technical: {
    title: "A strong technical answer",
    items: [
      {
        id: "specific",
        label: "Specific",
        check: "Does the answer name specific tools, techniques or concepts?",
      },
      {
        id: "why",
        label: "Says why",
        check: "Does the answer explain why, not only what?",
      },
      {
        id: "tradeoff",
        label: "A trade-off",
        check: "Does the answer weigh at least one trade-off or limitation?",
      },
      {
        id: "example",
        label: "A real example",
        check:
          "Does the answer ground the explanation in something the candidate has actually built or done?",
      },
    ],
  },
  motivation: {
    title: "A strong answer about you",
    items: [
      {
        id: "specific",
        label: "A specific reason",
        check:
          "Does the answer give a specific reason rather than a generic one anyone could give?",
      },
      {
        id: "fit",
        label: "Ties to this job",
        check: "Does the answer connect to this particular company or role?",
      },
      {
        id: "evidence",
        label: "Backed by your story",
        check: "Does the answer back the point with something from the candidate's own experience?",
      },
    ],
  },
};

/** At or above this Jev probability a checklist item counts as covered. */
export const PRACTICE_CHECK_MET = 0.5;

export const practiceAnswerChecklistSchema = z.object({
  kind: z.enum(PRACTICE_QUESTION_KINDS),
  items: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      met: z.boolean(),
      /** Jev's probability that the item is covered. */
      p: z.number().min(0).max(1),
    }),
  ),
});
export type PracticeAnswerChecklist = z.infer<typeof practiceAnswerChecklistSchema>;

/** The answer split into sentences, with the ones worth pointing at. Indexes into `sentences`. */
export const practiceAnswerMarkupSchema = z.object({
  sentences: z.array(z.string()),
  strongest: z.number().int().min(0).nullable(),
  weakest: z.number().int().min(0).nullable(),
  cuttable: z.array(z.number().int().min(0)),
});
export type PracticeAnswerMarkup = z.infer<typeof practiceAnswerMarkupSchema>;

export const practiceAnswerFeedbackSchema = z.object({
  version: z.literal(2),
  questionIndex: z.number().int().min(0),
  question: z.string(),
  answer: z.string(),
  attempt: z.number().int().min(1),
  rubric: z.array(practiceRubricScoreSchema),
  notes: z.array(practiceAnswerNoteSchema).max(3),
  /** The coach card shown between questions: one thing kept, one thing to change. */
  coach: z.object({ kept: z.string(), change: z.string() }),
  score: z.number().int().min(0).max(100),
  scorer: z.enum(["jev", "openai"]),
  metrics: practiceAnswerMetricsSchema,
  scoredAt: z.string(),
  scoreMs: z.number().int().min(0),
  /** Where the answer was given: in the interview (the default) or in a 60-second drill after it. */
  source: z.enum(["call", "drill"]).optional(),
  /** Feedback v3, when Jev scored the answer; absent on older answers and OpenAI fallbacks. */
  checklist: practiceAnswerChecklistSchema.optional(),
  markup: practiceAnswerMarkupSchema.optional(),
});
export type PracticeAnswerFeedback = z.infer<typeof practiceAnswerFeedbackSchema>;

export const PRACTICE_READINESS = ["keep_practising", "almost_there", "interview_ready"] as const;
export type PracticeReadiness = (typeof PRACTICE_READINESS)[number];
export const PRACTICE_READINESS_LABELS: Record<PracticeReadiness, string> = {
  keep_practising: "Keep practising",
  almost_there: "Almost there",
  interview_ready: "Interview ready",
};

export const practiceSessionFeedbackSchema = z.object({
  version: z.literal(2),
  readiness: z.enum(PRACTICE_READINESS),
  /** True when the score sits near a readiness boundary or the rubric confidence is low. */
  readinessCloseCall: z.boolean(),
  score: z.number().int().min(0).max(100),
  headline: z.string(),
  keep: z.object({ answerIndex: z.number().int(), quote: z.string(), note: z.string() }).nullable(),
  changeFirst: z.object({ text: z.string(), detail: z.string() }).nullable(),
  betterAnswer: z
    .object({
      answerIndex: z.number().int(),
      original: z.string(),
      revised: z.string(),
      why: z.string(),
    })
    .nullable(),
  facts: z.object({
    minutes: z.number().min(0),
    answered: z.number().int().min(0),
    questions: z.number().int().min(0),
    wordsPerMinute: z.number().int().nullable(),
    fillers: practiceAnswerMetricsSchema.shape.fillers,
    iCount: z.number().int().min(0),
    weCount: z.number().int().min(0),
  }),
  answers: z.array(practiceAnswerFeedbackSchema),
  notesOnYou: z
    .array(
      z.object({
        kind: z.enum(["pattern", "undersold", "coverage", "pace"]),
        title: z.string(),
        body: z.string(),
      }),
    )
    .max(4),
  coverage: z
    .array(
      z.object({
        requirement: z.string(),
        shownIn: z.array(z.number().int()),
        /** Answers that touched on it without really showing it (Jev, feedback v3). */
        partlyIn: z.array(z.number().int()).optional(),
      }),
    )
    .max(8),
  generatedAt: z.string(),
});
export type PracticeSessionFeedback = z.infer<typeof practiceSessionFeedbackSchema>;

export const practiceAnswerScore = (
  rubric: Pick<PracticeRubricScore, "dimension" | "level">[],
): number => {
  const weighted = rubric.reduce(
    (acc, item) => {
      const weight = PRACTICE_RUBRIC_WEIGHTS[item.dimension];
      return { sum: acc.sum + (item.level / 3) * weight, weight: acc.weight + weight };
    },
    { sum: 0, weight: 0 },
  );
  return weighted.weight ? Math.round((weighted.sum / weighted.weight) * 100) : 0;
};

/** Same bands as the marketing demo's feedback. */
export const PRACTICE_READINESS_BANDS = { ready: 80, almost: 60 } as const;

export const practiceReadinessFor = (score: number): PracticeReadiness =>
  score >= PRACTICE_READINESS_BANDS.ready
    ? "interview_ready"
    : score >= PRACTICE_READINESS_BANDS.almost
      ? "almost_there"
      : "keep_practising";

/** A whole answer rewritten in the candidate's words, with blanks for facts only they know. */
export const practiceRebuiltAnswerSchema = z.object({
  questionIndex: z.number().int().min(0),
  /** scoredAt of the answer it rebuilds: a newer answer makes it stale. */
  basedOn: z.string(),
  /** The text, with each blank written as [[1]], [[2]]... */
  text: z.string(),
  gaps: z.array(z.object({ ask: z.string() })).max(4),
  /** What changed and why, in a sentence. */
  why: z.string(),
  generatedAt: z.string(),
});
export type PracticeRebuiltAnswer = z.infer<typeof practiceRebuiltAnswerSchema>;
